import { MarkerWithLabel, MarkerWithLabelOptions } from '@googlemaps/markerwithlabel';
import { Box, Table, TableBody, TableCell, TableHead, TableRow } from '@mui/material';
import ToggleButtonGroup from '@mui/material/ToggleButtonGroup';
import { useTheme } from '@mui/material/styles';
import useMediaQuery from '@mui/material/useMediaQuery';
import { default as GoogleMapReact, Maps } from 'google-map-react';
import pluralize from 'pluralize';
import React, { memo, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import ReactDOMServer from 'react-dom/server';
import { useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import iconAtHome from '../../assets/images/at-home.svg';
import iconJobSite from '../../assets/images/job-site.svg';
import iconOnRoad from '../../assets/images/on-road.svg';
import logo from '../../assets/images/the-tag-system-logo.svg';
import { calcDaysFromNow, tagsDate } from '../../helpers/dataHelper';
import useSearchParams from '../../hooks/useSearchParams';
import { listProductTools } from '../../redux/slices/tools/toolsSlice';
import { useDispatch } from '../../redux/store';
import { ToggleButton } from '../../styled/components';
import './Common.css';
import { ControlType } from './FleetMap';
import { PickupStatus, iconForMaxDaysOnsite, iconForPickupStatus } from './jobSiteIcons';

type Marker = {
  key: string;
  lat: number;
  lng: number;
  siteName?: string;
  siteId?: string;
  vehicles?: number;
  toolsLeftBehind?: number;
  type: string;
  hideCaption?: boolean;
  recType?: string;
  warehouseId?: string;
  maxDaysOnsite?: number | null;
  status?: PickupStatus;
  startedAt?: string | null;
  pickupDate?: string | null;
  notes?: string | null;
  blowerCnt?: number;
  dehumidifierCnt?: number;
  airScrubberCnt?: number;
  otherCnt?: number;
};

export type ViewportBounds = {
  north: number;
  south: number;
  east: number;
  west: number;
};

interface IProps {
  markers?: Marker[];
  zoom: number;
  height?: number | string;
  withTraffic?: boolean;
  withFullscreen?: boolean;
  onFullScreen?: (fullScreen: boolean) => void;
  controls?: ControlType[];
  withLogo?: boolean;
  onMapInited?: VoidFunction;
  iconSize?: 'small' | 'medium';
  onBoundsChange?: (bounds: ViewportBounds) => void;
  // When set, the map auto-fit frames only markers of this rec_type (e.g. 'Job'),
  // so far-flung context markers (the home/company site, vehicles) don't blow the
  // zoom out. Falls back to all markers if none match.
  fitRecType?: string;
}

const GoogleMapReactWrapper = styled.div`
  width: 100%;
  min-height: 220px;
  height: 100%;
`;

const bootstrapURLKeys = {
  key: (
    window as unknown as {
      _env_: {
        REACT_APP_GOOGLE_MAP_API_KEY: string;
      };
    }
  )._env_.REACT_APP_GOOGLE_MAP_API_KEY,
  libraries: ['drawing'],
  version: '3.59',
};

const InternalMapComponent: React.FC<IProps> = ({
  markers,
  zoom,
  height,
  withTraffic,
  withFullscreen,
  withLogo,
  onFullScreen,
  controls,
  onMapInited,
  iconSize = 'medium',
  onBoundsChange,
  fitRecType,
}) => {
  const dispatch = useDispatch();
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const theme = useTheme();
  const mapRef = useRef<any>(); // eslint-disable-line
  const onBoundsChangeRef = useRef<IProps['onBoundsChange']>(onBoundsChange);
  useEffect(() => {
    onBoundsChangeRef.current = onBoundsChange;
  }, [onBoundsChange]);
  const isSmUp = useMediaQuery(theme.breakpoints.up('sm'), { noSsr: true });
  const markersRef = useRef<MarkerWithLabel[]>([]);
  const mapLoadedRef = useRef<boolean>(false);
  const trafficRef = useRef<HTMLButtonElement>(null);
  const mapViewRef = useRef<HTMLButtonElement>(null);
  const [trafficView, setTrafficView] = useState(false);
  const [mapView, setMapView] = useState('ROADMAP');
  const imgRef = useRef<HTMLImageElement>(null);
  const isMdUp = useMediaQuery(theme.breakpoints.up('md'), { noSsr: true });
  const [fullScreen, setFullScreen] = useState(false);

  const getMapOptions = useCallback(
    (maps: Maps) => {
      return {
        fullscreenControl: !!withFullscreen,
        mapTypeControl: false,
        mapTypeId: maps.MapTypeId.ROADMAP,
        scaleControl: isSmUp,
        scrollwheel: isSmUp,
        streetViewControl: false,
        draggable: isSmUp,
        disableDoubleClickZoom: !isSmUp,
      };
    },
    [isSmUp, withFullscreen],
  );

  useEffect(() => {
    if (onFullScreen) {
      onFullScreen(fullScreen);
    }
  }, [fullScreen, onFullScreen]);

  useEffect(() => {
    const onFullscreenChange = () => {
      const map = mapRef.current.map_;
      const maps = mapRef.current.maps_;
      const targetDiv = map?.getDiv()?.children[0];
      const fullScreen = document.fullscreenElement === targetDiv;
      if (targetDiv) {
        setFullScreen(fullScreen);
        targetDiv.style.zIndex = '0';
      }

      controls?.forEach((control) => {
        if (fullScreen && control.fullscreen) {
          map.controls[maps.ControlPosition[control.position]].push(control.ref.current);
        } else if (!fullScreen && control.fullscreen) {
          map.controls[maps.ControlPosition[control.position]].clear();
        }
      });
    };
    document.addEventListener('fullscreenchange', onFullscreenChange);

    return () => {
      document.removeEventListener('fullscreenchange', onFullscreenChange, false);
    };
  }, [getMapOptions, controls]);

  const renderMarkers = () => {
    if (!mapRef.current?.map_) {
      return;
    }
    const map = mapRef.current.map_;
    const maps = mapRef.current.maps_;

    const bounds = new maps.LatLngBounds();
    // Bounds restricted to fitRecType markers (when set) — used for the auto-fit
    // so context markers (home site, vehicles) don't widen the view.
    const focusBounds = new maps.LatLngBounds();
    let focusCount = 0;

    markersRef.current.forEach((marker) => {
      marker.setMap(null);
    });
    markersRef.current = [];

    const dataFromLocationType = (type: string, maxDaysOnsite?: number | null, status?: PickupStatus) => {
      const iconBySize = {
        Site: {
          small: [new maps.Point(10.56, 21.12), new maps.Size(21.12, 21.12)],
          medium: [new maps.Point(11, 22), new maps.Size(22, 22)],
        },
        Home: {
          small: [new maps.Point(10.56, 21.12), new maps.Size(21.12, 21.12)],
          medium: [new maps.Point(11, 22), new maps.Size(22, 22)],
        },
        Company_group: {
          small: [new maps.Point(10.56, 21.12), new maps.Size(21.12, 21.12)],
          medium: [new maps.Point(11, 22), new maps.Size(22, 22)],
        },
        Job_group: {
          small: [new maps.Point(10.56, 21.12), new maps.Size(21.12, 21.12)],
          medium: [new maps.Point(11, 22), new maps.Size(22, 22)],
        },
        Vehicle_group: {
          small: [new maps.Point(12.48, 24.96), new maps.Size(24.96, 24.96)],
          medium: [new maps.Point(13, 26), new maps.Size(26, 26)],
        },
        Vehicle: {
          small: [new maps.Point(12.48, 24.96), new maps.Size(24.96, 24.96)],
          medium: [new maps.Point(13, 26), new maps.Size(26, 26)],
        },
        default: {
          small: [new maps.Point(10.56, 21.12), new maps.Size(21.12, 21.12)],
          medium: [new maps.Point(11, 22), new maps.Size(22, 22)],
        },
      };

      let sizes = iconBySize[type][iconSize] ?? [new maps.Point(11, 22), new maps.Size(22, 22)];
      // Remediation jobsite pins (colored by pickup status) render 1.25x larger for
      // emphasis. Anchor stays at the bottom-center tip of the teardrop.
      if (status) {
        const base = sizes[1] as { width: number; height: number };
        const w = base.width * 1.25;
        const h = base.height * 1.25;
        sizes = [new maps.Point(w / 2, h), new maps.Size(w, h)];
      }
      const ageIcon = status ? iconForPickupStatus(status) : iconForMaxDaysOnsite(maxDaysOnsite);

      const locationType = {
        Site: {
          icon: new maps.MarkerImage(ageIcon, null, null, sizes[0], sizes[1]),
          link: '/customers/job-sites/',
        },
        Home: {
          icon: new maps.MarkerImage(iconAtHome, null, null, sizes[0], sizes[1]),
          link: '/customers/job-sites/',
        },
        Company_group: {
          icon: new maps.MarkerImage(iconAtHome, null, null, sizes[0], sizes[1]),
          link: '/company/sites/',
        },
        Job_group: {
          icon: new maps.MarkerImage(ageIcon, null, null, sizes[0], sizes[1]),
          link: '/customers/job-sites/',
        },
        Vehicle_group: {
          icon: new maps.MarkerImage(iconOnRoad, null, null, sizes[0], sizes[1]),
          link: '/company/vehicles/',
        },
        Vehicle: {
          icon: new maps.MarkerImage(iconOnRoad, null, null, sizes[0], sizes[1]),
          link: '/company/vehicles/',
        },
        default: {
          icon: new maps.MarkerImage(iconJobSite, null, null, sizes[0], sizes[1]),
          link: '/customers/job-sites/',
        },
      };

      return locationType[type];
    };

    markers?.forEach((marker) => {
      const position = {
        lat: marker.lat,
        lng: marker.lng,
      };

      const vehicles = marker.vehicles ? `${marker.vehicles} ${pluralize('Vehicle', marker.vehicles ?? 0)}` : '';
      const toolsLeftBehind = marker.toolsLeftBehind ? `${marker.toolsLeftBehind} ${pluralize('Items', marker.toolsLeftBehind ?? 0)}` : '';

      const count = `${vehicles}${vehicles && toolsLeftBehind ? ' & ' : ''}${toolsLeftBehind}`;

      const mapMarker = new MarkerWithLabel({
        position,
        map,
        icon: dataFromLocationType(marker.type, marker.maxDaysOnsite, marker.status)
          ? dataFromLocationType(marker.type, marker.maxDaysOnsite, marker.status).icon
          : dataFromLocationType('default', marker.maxDaysOnsite, marker.status).icon,
        labelContent: `<div class="short-label"><div><div>${marker.siteName}</div></div><div id="${marker.siteId}"></div>${
          count && `<div class="short-count">${count}</div>`
        }</div>`,
        labelAnchor: new maps.Point(0, 10),
        labelClass: `label short  ${marker.hideCaption ? 'none' : ''}`,
      } as MarkerWithLabelOptions);
      if (marker.hideCaption) {
        maps.event.addListener(mapMarker, 'mouseover', async () => {
          // eslint-disable-next-line
          // @ts-ignore
          const labelDiv = mapMarker.label.labelDiv;
          labelDiv.style.setProperty('display', 'block', 'important');
          labelDiv.style.setProperty('z-index', 10000);

          const tableDiv = document.getElementById(marker.siteId ?? '');
          // Remediation job sites carry pickup status + a machine summary in the
          // marker itself — render a static card, no per-item fetch needed.
          if (marker.status && tableDiv) {
            const machineTotal = (marker.blowerCnt ?? 0) + (marker.dehumidifierCnt ?? 0) + (marker.airScrubberCnt ?? 0) + (marker.otherCnt ?? 0);
            tableDiv.innerHTML = ReactDOMServer.renderToString(
              <Box
                sx={{
                  backgroundColor: (theme) => theme.palette.background.default,
                  borderRadius: 1,
                  padding: 1,
                  width: '100%',
                  mt: 2,
                  mb: 0.5,
                  textAlign: 'left',
                }}
              >
                <Table size="small" padding="none" className="identifier-table">
                  <TableBody>
                    <TableRow>
                      <TableCell>Days on Site</TableCell>
                      <TableCell>{marker.maxDaysOnsite ?? '—'}</TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell>Start Date</TableCell>
                      <TableCell>{tagsDate(marker.startedAt ?? undefined, 'DATE_MED', 'DATE_MED') ?? '—'}</TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell>Scheduled Pickup</TableCell>
                      <TableCell>{marker.pickupDate ? tagsDate(marker.pickupDate, 'DATE_MED', 'DATE_MED') : '—'}</TableCell>
                    </TableRow>
                  </TableBody>
                </Table>
                <Table size="small" padding="none" className="identifier-table">
                  <TableHead>
                    <TableRow>
                      <TableCell>Machine</TableCell>
                      <TableCell>Count</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    <TableRow>
                      <TableCell>Blower</TableCell>
                      <TableCell>{marker.blowerCnt ?? 0}</TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell>Dehumidifier</TableCell>
                      <TableCell>{marker.dehumidifierCnt ?? 0}</TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell>Air Scrubber</TableCell>
                      <TableCell>{marker.airScrubberCnt ?? 0}</TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell>Other</TableCell>
                      <TableCell>{marker.otherCnt ?? 0}</TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell>Total</TableCell>
                      <TableCell>{machineTotal}</TableCell>
                    </TableRow>
                  </TableBody>
                </Table>
                {marker.notes ? <Box sx={{ mt: 1 }}>Notes: {marker.notes}</Box> : null}
              </Box>,
            );
            return;
          }
          try {
            const res = await dispatch(
              listProductTools({
                productId: params.productId,
                ...(marker.recType === 'Vehicle' ? { trackingWarehouseId: marker.warehouseId } : { trackingSiteId: marker.siteId }),
              }),
            ).unwrap();
            if (res.data?.result && res.data?.result.length > 0 && tableDiv) {
              tableDiv.innerHTML = ReactDOMServer.renderToString(
                <Box sx={{ backgroundColor: (theme) => theme.palette.background.default, borderRadius: 1, padding: 1, width: '100%', mt: 2, mb: 0.5 }}>
                  <Table size="small" padding="none" className="identifier-table">
                    <TableHead>
                      <TableRow>
                        <TableCell>Identifier</TableCell>
                        <TableCell>Product</TableCell>
                        <TableCell>Last Seen Date</TableCell>
                        <TableCell>Days Onsite</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {res.data?.result.slice(0, 9).map((item) => (
                        <TableRow key={item.identifier}>
                          <TableCell>{item.identifier}</TableCell>
                          <TableCell>{item.product_name}</TableCell>
                          <TableCell>{tagsDate(item.processed_at)}</TableCell>
                          <TableCell>{calcDaysFromNow(item.processed_at)}</TableCell>
                        </TableRow>
                      ))}
                      {res.data?.result.length > 10 && (
                        <TableRow>
                          <TableCell colSpan={4} sx={{ color: (theme) => theme.palette.text.primary }}>
                            ...
                          </TableCell>
                        </TableRow>
                      )}
                    </TableBody>
                  </Table>
                </Box>,
              );
            }
          } catch (error) {
            console.log(error); // eslint-disable-line
          }
        });

        maps.event.addListener(mapMarker, 'mouseout', async () => {
          // eslint-disable-next-line
          // @ts-ignore
          const labelDiv = mapMarker.label.labelDiv;
          labelDiv.style.setProperty('display', 'none', 'important');
        });
      }

      maps.event.addListener(mapMarker, 'click', () => {
        const linkPrefix = dataFromLocationType(marker.type) ? dataFromLocationType(marker.type).link : dataFromLocationType('default').link;
        navigate(`${linkPrefix}${marker.siteId}/details${dataFromLocationType[marker.type]?.suffix ?? ''}`);
      });

      bounds.extend(position);
      if (!fitRecType || marker.recType === fitRecType) {
        focusBounds.extend(position);
        focusCount += 1;
      }

      markersRef.current.push(mapMarker);
    });
    // Always fit to the focus markers on a data change so no pin (e.g. a lone
    // ready/scheduled job site) ends up a speck in the corner because a far-off
    // home site/vehicle widened the bounds. Padding keeps edge pins clear of the
    // border. Panning doesn't re-run this (renderMarkers only fires on marker
    // changes), so the user's manual pan/zoom is preserved.
    const fitTo = focusCount > 0 ? focusBounds : bounds;
    if (focusCount === 1) {
      map.setCenter(focusBounds.getCenter());
      map.setZoom(13);
    } else if (focusCount > 1 || (focusCount === 0 && markers && markers.length > 1)) {
      map.fitBounds(fitTo, 60);
    } else if (markers?.length === 1) {
      map.setCenter(new maps.LatLng(markers[0].lat, markers[0].lng));
    }
  };

  const handleGoogleApiLoaded = async () => {
    if (!mapRef.current?.map_) {
      return;
    }
    mapLoadedRef.current = true;
    const map = mapRef.current.map_;
    const maps = mapRef.current.maps_;

    map.controls[maps.ControlPosition[withLogo ? 'TOP_RIGHT' : 'TOP_LEFT']].push(imgRef.current);
    map.controls[maps.ControlPosition.BOTTOM_CENTER].push(mapViewRef.current);
    map.controls[maps.ControlPosition.BOTTOM_CENTER].push(trafficRef.current);

    if (controls) {
      await Promise.all(
        controls?.map(async (control) => {
          if (!control.fullscreen) {
            await map.controls[maps.ControlPosition[control.position]].push(control.ref.current);
          }
        }),
      );
    }
    mapViewRef.current?.addEventListener('click', (event) => {
      const target = event.target as HTMLTextAreaElement;
      map.setMapTypeId(maps.MapTypeId[target.value]);
      setMapView(target.value);
    });

    map.trafficLayer = new maps.TrafficLayer();

    trafficRef.current?.addEventListener('click', () => {
      if (!map.trafficLayer.getMap()) {
        map.trafficLayer.setMap(map);
        setTrafficView(true);
      } else {
        map.trafficLayer.setMap(null);
        setTrafficView(false);
      }
    });
    map.mapTypes.hybrid.name = 'Satellite';

    if (onBoundsChangeRef.current) {
      const emitBounds = () => {
        const b = map.getBounds && map.getBounds();
        if (!b || !onBoundsChangeRef.current) return;
        const ne = b.getNorthEast();
        const sw = b.getSouthWest();
        onBoundsChangeRef.current({ north: ne.lat(), south: sw.lat(), east: ne.lng(), west: sw.lng() });
      };
      maps.event.addListener(map, 'idle', emitBounds);
    }

    renderMarkers();
    if (onMapInited) {
      onMapInited();
    }
  };

  const center = useMemo(() => {
    return markers && markers.length ? { lat: markers[0].lat, lng: markers[0].lng } : { lat: 40.7042975, lng: -73.7751181 };
  }, [markers]);

  useEffect(() => {
    if (mapLoadedRef.current) {
      renderMarkers();
    }
  }, [markers]); // eslint-disable-line

  return markers ? (
    <React.Fragment>
      <GoogleMapReactWrapper style={{ height }}>
        <GoogleMapReact
          ref={mapRef}
          yesIWantToUseGoogleMapApiInternals={true}
          options={getMapOptions}
          bootstrapURLKeys={bootstrapURLKeys}
          defaultCenter={center}
          defaultZoom={zoom}
          onGoogleApiLoaded={handleGoogleApiLoaded}
        />
      </GoogleMapReactWrapper>
      <img
        ref={imgRef}
        src={logo}
        style={{
          height: isMdUp ? 42 : 28,
          marginTop: 8,
          marginLeft: isMdUp ? 16 : 8,
          display: withLogo ? 'inline-block' : fullScreen ? 'inline-block' : 'none',
        }}
        alt="The Tag System"
      />
      <ToggleButtonGroup
        ref={mapViewRef}
        value={mapView}
        exclusive
        sx={{
          marginBottom: '10px',
          display: withTraffic ? 'inline-flex' : 'none',
        }}
      >
        <ToggleButton value="ROADMAP">Map</ToggleButton>
        <ToggleButton value="HYBRID">Satellite</ToggleButton>
      </ToggleButtonGroup>
      <ToggleButton
        ref={trafficRef}
        value="check"
        sx={{
          marginBottom: '10px',
          ml: 2,
          display: withTraffic ? 'inline-flex' : 'none',
        }}
        color="secondary"
        selected={trafficView}
      >
        Traffic
      </ToggleButton>
    </React.Fragment>
  ) : null;
};

export const LeftBehind = memo(InternalMapComponent);
